import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDashboardOverview,
  getSalesAnalytics,
  getCustomerAnalytics,
  refreshAnalyticsViews,
} from "../services/analyticsService";
import {
  AnalyticsFilter,
  CustomerAnalytics,
  DashboardOverview,
  SalesAnalytics,
} from "../types/analytics";
import { ApiResponse } from "../types/common";

export const analyticsKeys = {
  all: ["analytics"] as const,
  overview: (filter: AnalyticsFilter) =>
    [...analyticsKeys.all, "overview", filter] as const,
  sales: (filter: AnalyticsFilter) =>
    [...analyticsKeys.all, "sales", filter] as const,
  customers: (filter: AnalyticsFilter) =>
    [...analyticsKeys.all, "customers", filter] as const,
};

export const useDashboardOverview = (filter: AnalyticsFilter) => {
  return useQuery<ApiResponse<DashboardOverview>>({
    queryKey: analyticsKeys.overview(filter),
    queryFn: () => getDashboardOverview(filter),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSalesAnalytics = (filter: AnalyticsFilter) => {
  return useQuery<ApiResponse<SalesAnalytics>>({
    queryKey: analyticsKeys.sales(filter),
    queryFn: () => getSalesAnalytics(filter),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCustomerAnalytics = (filter: AnalyticsFilter) => {
  return useQuery<ApiResponse<CustomerAnalytics>>({
    queryKey: analyticsKeys.customers(filter),
    queryFn: () => getCustomerAnalytics(filter),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRefreshAnalytics = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshAnalyticsViews,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    },
  });
};
